'use client';

export interface BrowserMediaItem {
  id: string;
  name: string;
  path: string; // Base64 data URL
  file_type: 'image' | 'video' | 'lottie' | 'file';
  mime_type: string;
  size: number;
  created_at: string;
  width?: number;
  height?: number;
}

const DB_NAME = 'shopstag_browser_media_db';
const DB_VERSION = 1;
const STORE_NAME = 'media';
const FALLBACK_STORAGE_KEY = 'shopstag-browser-media-fallback-v1';

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      return reject(new Error('IndexedDB is not supported'));
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('created_at', 'created_at', { unique: false });
        store.createIndex('file_type', 'file_type', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Failed to open IndexedDB'));
  });
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Optimizes large raster images before storing them as Data URLs.
 * SVGs, GIFs, and small files are kept untouched.
 */
async function optimizeImageIfPossible(file: File): Promise<{ dataUrl: string; width?: number; height?: number; size: number }> {
  const isRaster = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp';
  const isSvgOrGif = file.type === 'image/svg+xml' || file.type === 'image/gif';

  if (!isRaster || isSvgOrGif || file.size < 500 * 1024) {
    const dataUrl = await fileToDataUrl(file);
    return { dataUrl, size: file.size };
  }

  return new Promise(async (resolve) => {
    try {
      const originalDataUrl = await fileToDataUrl(file);
      const img = new Image();
      img.onload = () => {
        const MAX_DIMENSION = 2048;
        let { width, height } = img;

        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          if (width > height) {
            height = Math.round((height * MAX_DIMENSION) / width);
            width = MAX_DIMENSION;
          } else {
            width = Math.round((width * MAX_DIMENSION) / height);
            height = MAX_DIMENSION;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          return resolve({ dataUrl: originalDataUrl, size: file.size, width: img.width, height: img.height });
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Convert to WebP if supported, fallback to JPEG
        const outputMime = file.type === 'image/png' ? 'image/png' : 'image/webp';
        const quality = outputMime === 'image/png' ? 0.9 : 0.85;
        const optimizedDataUrl = canvas.toDataURL(outputMime, quality);

        // Calculate approximate size in bytes from base64
        const base64Length = optimizedDataUrl.length - (optimizedDataUrl.indexOf(',') + 1);
        const approxSize = Math.round((base64Length * 3) / 4);

        // Only use optimized if it is actually smaller
        if (approxSize < file.size) {
          resolve({ dataUrl: optimizedDataUrl, width, height, size: approxSize });
        } else {
          resolve({ dataUrl: originalDataUrl, width: img.width, height: img.height, size: file.size });
        }
      };
      img.onerror = () => {
        resolve({ dataUrl: originalDataUrl, size: file.size });
      };
      img.src = originalDataUrl;
    } catch {
      const dataUrl = await fileToDataUrl(file);
      resolve({ dataUrl, size: file.size });
    }
  });
}

function determineFileType(file: File): BrowserMediaItem['file_type'] {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type === 'application/json' || file.name.endsWith('.json') || file.name.endsWith('.lottie')) return 'lottie';
  return 'file';
}

// Fallback for environments where IndexedDB is blocked
function getFallbackList(): BrowserMediaItem[] {
  if (typeof window === 'undefined') return [];
  try {
    return JSON.parse(window.localStorage.getItem(FALLBACK_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveFallbackItem(item: BrowserMediaItem): void {
  if (typeof window === 'undefined') return;
  try {
    const list = getFallbackList().filter((i) => i.id !== item.id);
    list.unshift(item);
    window.localStorage.setItem(FALLBACK_STORAGE_KEY, JSON.stringify(list));
  } catch {
    // LocalStorage quota reached
  }
}

function deleteFallbackItem(id: string): void {
  if (typeof window === 'undefined') return;
  try {
    const list = getFallbackList().filter((i) => i.id !== id);
    window.localStorage.setItem(FALLBACK_STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Ignore
  }
}

/**
 * Saves a file from the user's computer into the browser's persistent IndexedDB storage.
 */
export async function saveBrowserMedia(file: File): Promise<BrowserMediaItem> {
  const { dataUrl, width, height, size } = await optimizeImageIfPossible(file);
  const fileType = determineFileType(file);
  const now = new Date().toISOString();
  const id = `local_media_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

  const item: BrowserMediaItem = {
    id,
    name: file.name,
    path: dataUrl,
    file_type: fileType,
    mime_type: file.type || 'application/octet-stream',
    size,
    created_at: now,
    width,
    height,
  };

  try {
    const db = await openDatabase();
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const req = store.put(item);
      req.onsuccess = () => resolve(item);
      req.onerror = () => reject(req.error || new Error('Failed to save to IndexedDB'));
    });
  } catch (err) {
    saveFallbackItem(item);
    return item;
  }
}

/**
 * Adds an external image by URL into browser media for persistent local usage.
 */
export async function saveBrowserMediaFromUrl(name: string, url: string): Promise<BrowserMediaItem> {
  const id = `local_url_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  const now = new Date().toISOString();

  const item: BrowserMediaItem = {
    id,
    name: name || 'Zewnętrzny obraz',
    path: url,
    file_type: 'image',
    mime_type: 'image/jpeg',
    size: 0,
    created_at: now,
  };

  try {
    const db = await openDatabase();
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const req = store.put(item);
      req.onsuccess = () => resolve(item);
      req.onerror = () => reject(req.error || new Error('Failed to save URL to IndexedDB'));
    });
  } catch {
    saveFallbackItem(item);
    return item;
  }
}

/**
 * Retrieves all stored browser media items sorted by newest first.
 */
export async function getBrowserMediaList(): Promise<BrowserMediaItem[]> {
  try {
    const db = await openDatabase();
    return await new Promise((resolve) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const req = store.getAll();
      req.onsuccess = () => {
        const results: BrowserMediaItem[] = req.result || [];
        results.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        resolve(results);
      };
      req.onerror = () => {
        resolve(getFallbackList());
      };
    });
  } catch {
    return getFallbackList();
  }
}

/**
 * Deletes an item by its ID from browser media storage.
 */
export async function deleteBrowserMedia(id: string): Promise<void> {
  deleteFallbackItem(id);
  try {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const req = store.delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // Ignore IndexedDB error if fallback succeeded
  }
}

/**
 * Clears all browser media stored locally.
 */
export async function clearBrowserMedia(): Promise<void> {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(FALLBACK_STORAGE_KEY);
    } catch {
      // Ignore
    }
  }
  try {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const req = store.clear();
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // Ignore
  }
}
